// ============================================================
// MagazineControl - the footer's magazine gauge (v8.235.0).
//
// The reload button and the 'Ammo: x / y' line, merged into one
// instrument: the drawn magazine (AmmoMeter - pips while they fit,
// a bar for belts), the live count, and the reload cost stencil.
// Clicking it reloads. A FULL magazine dims the control and the
// click does NOTHING - reloading used to fire anyway and silently
// drop a prepared aim. A refill flashes briefly (reduced-motion
// suppressed). Melee weapons have no magazine and render nothing.
// ============================================================
import { useMemo } from 'react';
import useBriefly from '../hooks/useBriefly';
import AmmoMeter from './armoury/AmmoMeter';
import clsx from '../utils/clsx';
import styles from '../styles/FireSelector.module.css';
import type { Slot } from '../types';

interface Props {
  slot: Slot;
  isMelee: boolean;
  onReload: () => void;
}

/** A jump to full lights the refill flash for ~400ms. */
const filled = (was: { current: number }, now: { current: number; max: number }) =>
  now.current === now.max && was.current < now.max;

export default function MagazineControl({ slot, isMelee, onReload }: Props) {
  const current = slot.ammo?.current ?? 0;
  const max = slot.ammo?.max ?? 0;
  const full = current >= max;
  const reloadAction = slot.ammo?.reloadAction || 'Full';

  const rounds = useMemo(() => ({ current, max }), [current, max]);
  const refilling = useBriefly(rounds, filled, 400);

  if (isMelee || max <= 0) return null;

  return (
    <button
      type="button"
      data-testid="magazine-control"
      className={clsx(styles.magazine, full && styles.magazineFull, refilling && styles.refill)}
      aria-label={`Reload ${slot.weaponName} (${reloadAction} action)`}
      aria-disabled={full}
      title={full ? 'Magazine full' : `Reload - ${reloadAction} action`}
      onClick={() => {
        // The guard is behavioural, not just visual: a redundant reload
        // still costs the action and drops a prepared aim.
        if (!full) onReload();
      }}
    >
      <AmmoMeter ammo={slot.ammo} />
      <span className={styles.reloadStencil}>RELOAD - {reloadAction.toUpperCase()}</span>
    </button>
  );
}
